import { computed } from 'vue'
import {
  formatCallDuration,
  formatCost,
  formatIsoDate,
  formatNumber,
  formatRupee,
} from '@/utils/format'

function getGreeting() {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good Morning'
  if (hour < 17) return 'Good Afternoon'
  return 'Good Evening'
}

function parseRowDate(value) {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

function filterRowsByChartPeriod(rows, period) {
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  return rows.filter((row) => {
    const d = parseRowDate(row.date)
    if (!d) return false
    if (period === 'today') {
      return d >= todayStart
    }
    if (period === '7d') {
      const cutoff = new Date(todayStart)
      cutoff.setDate(cutoff.getDate() - 6)
      return d >= cutoff
    }
    return true
  })
}

function sumField(rows, field) {
  return rows.reduce((acc, row) => acc + (Number(row[field]) || 0), 0)
}

function trendFromDailyRows(rows, field) {
  // dailyRows is sorted most-recent-first; compare the latest day that
  // actually has data against the one before it -- a real day-over-day
  // change, not a hardcoded percentage.
  const withData = rows.filter((r) => r?.date)
  if (withData.length < 2) return { label: '—', up: null }

  const current = Number(withData[0]?.[field]) || 0
  const previous = Number(withData[1]?.[field]) || 0

  if (previous === 0) {
    if (current === 0) return { label: 'Stable', up: null }
    return { label: 'New', up: true }
  }

  const pct = Math.round(((current - previous) / previous) * 100)
  if (pct === 0) return { label: 'Stable', up: null }
  return { label: `${pct > 0 ? '+' : ''}${pct}%`, up: pct > 0 }
}

function formatPeakHour(hour) {
  if (hour == null || Number.isNaN(hour)) return 'No calls yet'
  const start = new Date(2000, 0, 1, hour)
  const end = new Date(2000, 0, 1, hour + 1)
  const fmt = (d) =>
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `${fmt(start)} – ${fmt(end)}`
}

export function useDashboardMetrics({
  dashboard,
  organization,
  userName,
  dailyRows,
  chartPeriod,
  chartMetric,
  insights: dashboardInsights,
}) {
  const greeting = computed(() => getGreeting())

  const displayName = computed(() => {
    const name = userName.value?.trim()
    if (name) return name.split(/\s+/)[0]
    return 'there'
  })

  const heroKpis = computed(() => {
    const org = organization.value ?? {}
    const d = dashboard.value ?? {}
    const i = dashboardInsights.value ?? {}

    return [
      {
        key: 'total_calls',
        label: 'Total Calls',
        value: formatNumber(i.total_calls ?? d.total_calls ?? org.total_number_of_calls),
        icon: 'mdi-phone-in-talk',
      },
      {
        key: 'answered',
        label: 'Calls Answered',
        // Real connected-call count (duration_seconds > 0) for the period,
        // from getDashboardInsights. KAN-43: this used to fall back to
        // d.total_calls/org.total_number_of_calls exactly like Total Calls
        // does above -- meaning if the separate getDashboardInsights call
        // ever failed on its own (it's fetched independently and treated as
        // best-effort, see useDashboardData) while the main dashboard call
        // still succeeded, this card silently went right back to showing
        // the exact "= Total Calls" bug its own comment says was already
        // fixed once, implying a false 100% answer rate with no error
        // indicator. No non-insights source in this codebase actually knows
        // the connected/answered count (that distinction doesn't exist on
        // `dashboard`/`organization` at all) -- so when insights hasn't
        // loaded, showing "—" is honest; showing Total Calls is not.
        value: i.connected_calls != null ? formatNumber(i.connected_calls) : '—',
        icon: 'mdi-phone-check',
      },
      {
        key: 'agents',
        label: 'Active AI Agents',
        value: '1',
        icon: 'mdi-robot-outline',
      },
      {
        key: 'leads',
        label: 'Leads Captured',
        // KAN-43: this used to sum total_unique_callers across every day in
        // the month -- that field is only deduped WITHIN each day (reset to
        // [] daily on the backend), so a caller who phoned in on more than
        // one day that month was counted once per day, silently inflating
        // this card. unique_leads_captured (from getDashboardInsights) is a
        // real distinct-caller count over the whole month, computed the
        // same way the rest of this codebase does it elsewhere.
        value: i.unique_leads_captured != null ? formatNumber(i.unique_leads_captured) : '—',
        icon: 'mdi-account-star-outline',
      },
      {
        key: 'availability',
        label: 'Availability',
        value: '24/7',
        icon: 'mdi-clock-check-outline',
      },
    ]
  })

  const operationsMetrics = computed(() => {
    const d = dashboard.value
    if (!d) return []
    const todayRows = filterRowsByChartPeriod(dailyRows.value, 'today')
    const callsToday = sumField(todayRows, 'total_calls')

    const callsTrend = trendFromDailyRows(dailyRows.value, 'total_calls')
    const talkTimeTrend = trendFromDailyRows(dailyRows.value, 'total_call_seconds')
    const costTrend = trendFromDailyRows(dailyRows.value, 'total_cost')

    return [
      {
        key: 'calls_today',
        label: 'Calls Today',
        value: formatNumber(callsToday),
        icon: 'mdi-phone-incoming',
        trend: callsTrend.label,
        trendUp: callsTrend.up,
      },
      {
        key: 'talk_time',
        label: 'Talk Time',
        value: formatCallDuration(d.total_call_seconds),
        icon: 'mdi-timer-sand',
        trend: talkTimeTrend.label,
        trendUp: talkTimeTrend.up,
      },
      {
        key: 'avg_duration',
        label: 'Avg Duration',
        value: formatCallDuration(
          d.total_calls > 0 ? d.total_call_seconds / d.total_calls : 0,
        ),
        icon: 'mdi-chart-timeline-variant',
        trend: 'Stable',
        trendUp: null,
      },
      {
        key: 'total_cost',
        label: 'Total Cost',
        value: formatCost(d.total_cost),
        icon: 'mdi-currency-usd',
        trend: costTrend.label,
        trendUp: costTrend.up,
      },
      {
        key: 'credits',
        label: 'Credits Balance',
        value: formatNumber(d.credits_balance),
        icon: 'mdi-wallet-outline',
        trend: 'Healthy',
        trendUp: true,
      },
      {
        key: 'cpm',
        label: 'Cost / Minute',
        value:
          organization.value?.voice_agent_cost_per_minute != null
            ? formatRupee(organization.value.voice_agent_cost_per_minute)
            : '—',
        icon: 'mdi-speedometer',
        trend: 'Optimal',
        trendUp: true,
      },
    ]
  })

  const financialMetrics = computed(() => {
    const d = dashboard.value ?? {}
    const org = organization.value ?? {}

    return [
      {
        key: 'balance',
        label: 'Credits Balance',
        value: formatNumber(d.credits_balance ?? org.credits_balance),
        sub: 'Available now',
        large: true,
      },
      {
        key: 'added',
        label: 'Credits Added',
        value: formatNumber(d.total_credits_added),
        sub: 'This period',
      },
      {
        key: 'rental',
        label: 'Rental Deductions',
        value: formatNumber(d.total_rental_deductions),
        sub: 'This period',
      },
      {
        key: 'daily_rental',
        label: 'Daily Rental',
        value:
          org.daily_rental != null ? formatRupee(org.daily_rental) : '—',
        sub: 'Per day',
      },
      {
        key: 'cpm',
        label: 'Cost Per Minute',
        value:
          org.voice_agent_cost_per_minute != null
            ? formatRupee(org.voice_agent_cost_per_minute)
            : '—',
        sub: 'Voice agent',
      },
      {
        key: 'monthly_usage',
        label: 'Monthly Usage',
        value: formatCost(d.total_cost),
        sub: 'Total spend',
      },
    ]
  })

  const filteredChartRows = computed(() => {
    const sorted = [...dailyRows.value].sort((a, b) =>
      String(a?.date ?? '').localeCompare(String(b?.date ?? '')),
    )
    return filterRowsByChartPeriod(sorted, chartPeriod.value)
  })

  const chartPoints = computed(() => {
    const rows = filteredChartRows.value
    if (!rows.length) return []

    const metricKey =
      chartMetric.value === 'duration'
        ? 'total_call_seconds'
        : chartMetric.value === 'cost'
          ? 'total_cost'
          : chartMetric.value === 'credits'
            ? 'total_cost'
            : 'total_calls'

    return rows.map((row) => ({
      label: formatIsoDate(row.date).split(',')[0],
      value: Number(row[metricKey]) || 0,
      raw: row,
    }))
  })

  const insights = computed(() => {
    const rows = dailyRows.value
    const i = dashboardInsights.value

    if (!rows.length) {
      return {
        score: null,
        peakDay: '—',
        peakHour: 'No calls yet',
        activePeriod: '—',
      }
    }

    let peakRow = rows[0]
    rows.forEach((row) => {
      if ((Number(row.total_calls) || 0) > (Number(peakRow.total_calls) || 0)) {
        peakRow = row
      }
    })

    return {
      // Real AI Score: 100% minus the share of this period's calls flagged
      // "needs_review" by the actual call-feedback/review system (AI
      // auto-analysis + human flags) -- not a function of call volume.
      score: i?.ai_score ?? null,
      peakDay: formatIsoDate(peakRow.date),
      // Real peak hour computed from actual call timestamps for the period
      // (getDashboardInsights), in the org's own timezone.
      peakHour: formatPeakHour(i?.peak_hour),
      activePeriod: 'Business hours',
    }
  })

  const activityRows = computed(() =>
    dailyRows.value.map((row, index) => {
      const prev = dailyRows.value[index + 1]
      const current = Number(row.total_calls) || 0
      const previous = Number(prev?.total_calls) || 0
      let trend = 'stable'
      if (current > previous) trend = 'up'
      if (current < previous) trend = 'down'

      return {
        ...row,
        trend,
      }
    }),
  )

  return {
    greeting,
    displayName,
    heroKpis,
    operationsMetrics,
    financialMetrics,
    chartPoints,
    insights,
    activityRows,
  }
}
